import React, { useEffect, useRef, useState } from 'react';
import {
  INITIAL_HEIGHT,
  INITIAL_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH,
  ResizeDir,
} from '../types';

type Params = {
  isOpen: boolean;
  snapToBottom: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
};

export const useWindowBehavior = ({
  isOpen,
  snapToBottom,
  inputRef,
}: Params) => {
  const [pos, setPos] = useState({
    x:
      typeof window !== 'undefined'
        ? window.innerWidth - INITIAL_WIDTH - 14
        : 100,
    y: 45,
  });
  const [size, setSize] = useState({
    width: INITIAL_WIDTH,
    height: INITIAL_HEIGHT,
  });

  const dragState = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);

  useEffect(() => {
    const handleWindowResize = () => {
      setPos((prev) => ({
        x: Math.max(0, Math.min(prev.x, window.innerWidth - MIN_WIDTH)),
        y: Math.max(0, Math.min(prev.y, window.innerHeight - MIN_HEIGHT)),
      }));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPos({
        x: Math.max(0, window.innerWidth - INITIAL_WIDTH - 14),
        y: 45,
      });
      setSize({ width: INITIAL_WIDTH, height: INITIAL_HEIGHT });
    } else {
      setTimeout(() => inputRef.current?.focus(), 50);
      snapToBottom();
    }
  }, [isOpen, snapToBottom]); // inputRef is a stable ref object, not a dep

  const handleDragBarMouseDown = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest('button')) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
    };
    const onMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      const newX =
        dragState.current.startPosX + ev.clientX - dragState.current.startX;
      const newY =
        dragState.current.startPosY + ev.clientY - dragState.current.startY;
      setPos({
        x: Math.max(0, Math.min(newX, window.innerWidth - size.width)),
        y: Math.max(0, Math.min(newY, window.innerHeight - size.height)),
      });
    };
    const onUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const makeResizeHandler = (dir: ResizeDir) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.width;
    const startH = size.height;
    const startPosX = pos.x;
    const startPosY = pos.y;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      let newW = startW;
      let newH = startH;
      let newX = startPosX;
      let newY = startPosY;

      if (dir.includes('e'))
        newW = Math.min(
          Math.max(MIN_WIDTH, startW + dx),
          window.innerWidth - startPosX,
        );
      if (dir.includes('s'))
        newH = Math.min(
          Math.max(MIN_HEIGHT, startH + dy),
          window.innerHeight - startPosY,
        );
      if (dir.includes('w')) {
        newW = Math.min(
          Math.max(MIN_WIDTH, startW - dx),
          startPosX + startW, // left edge can't go past x=0
        );
        newX = startPosX + (startW - newW);
      }
      if (dir.includes('n')) {
        newH = Math.min(
          Math.max(MIN_HEIGHT, startH - dy),
          startPosY + startH, // top edge can't go past y=0
        );
        newY = startPosY + (startH - newH);
      }

      setSize({ width: newW, height: newH });
      setPos({ x: newX, y: newY });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return { pos, size, handleDragBarMouseDown, makeResizeHandler };
};
